import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useTableSort } from "@/lib/tableSort";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Search, ChevronDown, ChevronRight, Phone } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAVY = "#0F2C59";
const AMBER = "#F59E0B";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return "—"; }
};

export const UnmarkedFirms = () => {
  const { user } = useAuth();
  const showContact = !!user?.is_owner;
  const umT = useTableSort([]);
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState({});

  useEffect(() => {
    api.get("/dashboard/unmarked-firms")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("İşaretsiz firmalar yüklenemedi"));
  }, []);

  const branches = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toLocaleLowerCase("tr");
    if (!term) return data.branches;
    return data.branches
      .map((b) => ({
        ...b,
        committees: b.committees
          .map((c) => ({ ...c, firms: c.firms.filter((f) => (f.company_name || "").toLocaleLowerCase("tr").includes(term) || String(f.sicil_no || "").includes(term)) }))
          .filter((c) => c.firms.length > 0 || (c.name || "").toLocaleLowerCase("tr").includes(term) || String(c.committee_no) === term),
      }))
      .filter((b) => b.committees.length > 0 || b.branch.toLocaleLowerCase("tr").includes(term));
  }, [data, q]);

  if (!data) return <Skeleton className="h-96 w-full" />;

  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));

  return (
    <div className="space-y-4" data-testid="unmarked-firms">
      <div className="grid grid-cols-3 gap-3">
        {[
          { l: "Hiç İşaretlenmemiş Firma", v: data.total, c: AMBER },
          { l: "Telefonu Olan", v: data.with_phone, c: NAVY, hide: !showContact },
          { l: "Sorumlusu Atanmış", v: data.with_responsible, c: "#16A34A" },
        ].filter(Boolean).map((k) => (
          <Card key={k.l} className="p-3" data-testid={`uf-kpi-${k.l}`}>
            <div className="text-xl font-bold tabular-nums" style={{ color: k.c }}>{k.v}</div>
            <div className="text-xs text-muted-foreground">{k.l}</div>
          </Card>
        ))}
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input className="pl-9" placeholder="Firma, sicil no veya komite ara..." value={q}
          onChange={(e) => setQ(e.target.value)} data-testid="uf-search" />
      </div>

      <div className="space-y-3">
        {branches.map((b) => (
          <Card key={b.branch} className="overflow-hidden" data-testid={`uf-branch-${b.branch}`}>
            <div className="flex items-center justify-between gap-2 px-4 py-2.5 bg-muted/50 border-b border-border">
              <p className="font-semibold text-sm">{b.branch}</p>
              <Badge variant="outline" style={{ color: AMBER, borderColor: AMBER }}>{b.count} firma</Badge>
            </div>
            <div className="divide-y divide-border/50">
              {b.committees.map((c) => {
                const key = `${b.branch}-${c.committee_no}`;
                const isOpen = !!open[key];
                return (
                  <div key={key}>
                    <button type="button" onClick={() => toggle(key)}
                      className="w-full flex items-center gap-2 px-4 py-2 text-left text-xs hover:bg-muted/30"
                      data-testid={`uf-committee-${key}`}>
                      {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      <span className="font-medium">{c.committee_no ? `${c.committee_no}. ${c.name}` : "Komite atanmamış"}</span>
                      <span className="ml-auto tabular-nums text-muted-foreground">{c.count} firma</span>
                    </button>
                    {isOpen && (
                      <div className="overflow-x-auto bg-background">
                        <table className="w-full text-xs">
                          <thead className="text-muted-foreground border-y border-border">
                            <tr>
                              <umT.Th sortKey="um-firma" get={(r) => r.company_name} text className="text-left font-medium py-1.5 px-3">Firma</umT.Th>
                              <umT.Th sortKey="um-sicil" get={(r) => r.sicil_no} text className="text-left font-medium py-1.5 px-3">Sicil No</umT.Th>
                              {showContact && <umT.Th sortKey="um-tel" get={(r) => r.phone} text className="text-left font-medium py-1.5 px-3">Telefon</umT.Th>}
                              <umT.Th sortKey="um-sorumlu" get={(r) => r.responsible || r.team_assignment_2026} text className="text-left font-medium py-1.5 px-3">Sorumlu</umT.Th>
                              <umT.Th sortKey="um-guncelleme" get={(r) => r.updated_at} text className="text-left font-medium py-1.5 px-3">Son güncelleme</umT.Th>
                            </tr>
                          </thead>
                          <tbody>
                            {umT.applySort(c.firms).map((f) => (
                              <tr key={f.id} className="border-b border-border/40 hover:bg-muted/20"
                                data-testid={`uf-firm-${f.id}`}>
                                <td className="py-1.5 px-3 font-medium">{f.company_name}</td>
                                <td className="py-1.5 px-3 text-muted-foreground">{f.sicil_no || "—"}</td>
                                {showContact && (
                                <td className="py-1.5 px-3">
                                  {f.phone ? (
                                    <a href={`tel:${f.phone}`} className="inline-flex items-center gap-1 text-sky-700 dark:text-sky-400 hover:underline">
                                      <Phone className="h-3 w-3" />{f.phone}
                                    </a>
                                  ) : <span className="text-muted-foreground">—</span>}
                                </td>
                                )}
                                <td className="py-1.5 px-3">{f.responsible || <span className="text-muted-foreground">atanmamış</span>}</td>
                                <td className="py-1.5 px-3 text-muted-foreground">{fmtDate(f.last_update)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        ))}
        {branches.length === 0 && (
          <Card className="p-10 text-center text-sm text-muted-foreground">Kayıt bulunamadı</Card>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Bu listede 2026 oy tahmini hiç girilmemiş FAAL firmalar yer alır. Şube başlığına ait komite satırına
        dokununca firma listesi açılır; firmalar alfabetik sıralıdır.
      </p>
    </div>
  );
};
